import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SectionHeading from "@/components/shared/SectionHeading";
import { slideInLeft, slideInRight } from "@/constants/animations";
import WhyUsSection from "@/components/home/WhyUsSection";

const item = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};
const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15 } },
};

export default function About() {
  return (
    <div data-testid="about-page">
      <Helmet>
        <title>À propos — Pierre Affrètement | Commissionnaire de transport Vichy</title>
        <link rel="canonical" href="https://pierreaffretement.fr/about" />
        <meta name="description" content="Découvrez Pierre Affrètement, commissionnaire de transport basé à Vichy (03) depuis 2021. Notre histoire, nos engagements et notre équipe." />
      </Helmet>
      {/* Hero About */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden bg-[#1a2e48]">
        <div className="absolute inset-0">
          <img
            src="/images/about-hero-bg.png"
            alt="Nœud autoroutier européen"
            className="w-full h-full object-cover opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a2e48]/30 via-[#1a2e48]/50 to-[#1a2e48]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 pt-44">
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="max-w-3xl"
          >
            <motion.div variants={item} className="flex items-center gap-3 mb-6">
              <div className="h-px w-10 bg-[#E8902A]" />
              <span className="font-heading font-bold text-xs tracking-widest uppercase text-[#E8902A]">
                Qui sommes-nous
              </span>
            </motion.div>
            <motion.h1
              variants={item}
              className="font-heading font-black text-5xl md:text-6xl lg:text-7xl text-white leading-[0.95] uppercase mb-6"
            >
              La Force de notre Réseau,
              <br />
              <span className="gradient-text">au service</span>
              <br />
              <span className="text-5xl md:text-6xl font-heading font-bold text-slate-200">
                de vos flux.
              </span>
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Notre histoire */}
      <section className="py-24 bg-[#1e3a52]" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...slideInLeft}>
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px w-10 bg-[#E8902A]" />
                <span className="font-heading font-bold text-xs tracking-widest uppercase text-[#E8902A]">
                  Notre histoire
                </span>
              </div>
              <h2 className="font-heading font-black text-4xl md:text-5xl text-white leading-tight mb-6">
                Basé à <span className="gradient-text">Vichy (03)</span>
              </h2>

              <p className="text-slate-300 text-base leading-relaxed mb-5">
                <strong className="text-white">PIERRE AFFRÈTEMENT</strong> Commissionnaire de transport à Vichy (03) depuis 2021, spécialiste en organisation de transports de marchandises vous propose ses solutions techniques, adaptées et compétitives pour vos expéditions nationales et internationales.
              </p>
              <p className="text-slate-400 text-base leading-relaxed mb-5">
                La croissance continue de notre entreprise depuis sa création témoigne de notre engagement quotidien, de la fiabilité de notre réseau national et européen, de notre capacité à satisfaire nos clients et partenaires, et de la confiance que ceux-ci nous portent en retour. C’est dans ce cercle vertueux que nous souhaitons voir s’inscrire nos collaborations actuelles et futures.
              </p>
              <p className="text-slate-300 text-base leading-relaxed mb-8">
                Notre <strong className="text-white font-semibold">Engagement</strong> pour vous accompagner, l’<strong className="text-white font-semibold">Ambition</strong> constante de vous satisfaire et la <strong className="text-white font-semibold">Confiance</strong> que nous suscitons par nos expertises métiers incarnent nos valeurs, déterminent notre cap et constituent notre singularité.
              </p>

              <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl border border-white/10 bg-white/[0.03]">
                <div>
                  <div className="text-slate-300 text-sm font-normal">Pierre Moubamba-Pizon</div>
                  <div className="text-slate-500 text-xs">Président</div>
                </div>
              </div>
            </motion.div>

            <motion.div {...slideInRight} className="relative">
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="/images/about-bg.png"
                  alt="Solution Transport — Vichy France 2021"
                  className="w-full h-[420px] object-cover object-center"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a52]/40 to-transparent" />
              </div>

              {/* badge supprimé */}
              <div className="absolute -top-4 -right-4 glass-card rounded-2xl px-6 py-4 border border-white/10">
                <div className="text-white text-xs font-heading uppercase tracking-widest mb-1">Fondé en</div>
                <div className="font-heading font-black text-3xl gradient-text">2021</div>
              </div>
              <div className="absolute -bottom-5 left-5 glass-card rounded-2xl px-6 py-4 border border-white/10 text-center">
                <div className="text-white text-xs font-heading uppercase tracking-widest mb-1">Transport</div>
                <div className="font-heading font-black text-3xl gradient-text">Multimodal</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pourquoi nous choisir + Nos engagements */}
      <WhyUsSection />

    </div>
  );
}
